import {
    Component,
    ComponentInterface,
    Event,
    Element,
    EventEmitter,
    Prop,
    Watch,
} from "@stencil/core";
import api from "../../utils/api";
import serializeForm from "../../utils/serializeForm";
import { ProductSearchResponse } from "./product-search-response";
import applyElementPolyfills from "../../polyfills/element";

applyElementPolyfills();

@Component({
    tag: "ux-iws-data-controller-shop-product-search",
    styleUrl: "data-controller-shop-product-search.scss",
})
export class DataControllerShopProductSearch implements ComponentInterface {
    @Element() domNode: HTMLUxIwsDataControllerShopProductSearchElement;
    /** Custom event to trigger pre data calls */
    @Event({
        eventName: "uxBeforeDataSearchCall",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    beforeDataSearchCall: EventEmitter;

    /** Custom event to trigger post data calls */
    @Event({
        eventName: "uxAfterDataSearchCall",
        composed: false,
        cancelable: false,
        bubbles: false,
    })
    afterDataSearchCall: EventEmitter<{
        url: string;
        shopArea: string;
        totalCount: number;
        isPaging: boolean;
        selectedMachine: string;
    }>;

    /** describes for which page the controller is used for. Necessary for further search calls*/
    @Prop() pageType: string = "";

    /** endpoint where product result can be requested */
    @Prop() searchUrl: string = "";

    /** CSS selector for adding and updating the product result from search url*/
    @Prop() searchResultContainerSelector: string = "";

    /** CSS selector for adding and updating the category tree from tree url*/
    @Prop() categoryTreeContainerSelector: string = "";

    /** CSS selector for adding and updating the breadcrumb via tree url*/
    @Prop() breadcrumbContainerSelector: string = "";

    /** CSS selector for adding and updating the category description text via  tree url*/
    @Prop() categoryDescriptionSelector: string = "";

    /** CSS selector for defining all links which should trigger a search on click events*/
    @Prop() linkSelector: string = "";

    /** CSS selector for defining all elements which should trigger a search on submit events*/
    @Prop() submitSelector: string = "";

    /** CSS selector for defining all elements which should trigger a search on submit events*/
    @Prop() sortSelector: string = "";

    /** CSS selector for the ux tab element where the total number found is added as suffix concatenated with the totalNumberPrefix */
    @Prop() uxTabSelector: string = "";

    /** CSS selector for the scroll-sensor */
    @Prop() scrollSensorSelector: string = "";

    /** Prefix text value on updating uxTab Name */
    @Prop() totalNumberPrefix: string = "";

    /** Container where the search result should be added    */
    private searchResultContainer: HTMLElement | null = null;

    /** Container where the category tree should be added     */
    private categoryTreeContainer: HTMLElement | null = null;

    /** Container where the category description should be added    */
    private categoryDescContainer: HTMLElement | null = null;

    /** Container where the breadcrumb should be added    */
    private breadCrumbContainer: HTMLElement | null = null;

    /** form element */
    private triggeredForm: HTMLFormElement | null = null;

    /** UX-Tab as HTMLUxTabElement where the updated total number of orders should be put */
    private uxTabElement: HTMLUxTabElement | null = null;

    /** URL for getting the next page */
    private nextPage: string = "";

    /** DOM element where all the table rows should be put */
    private scrollSensor: HTMLUxInfiniteScrollSensorElement | null = null;

    private isCategoryTreeLoaded: boolean | false;

    private isSearchResultPageLoaded: boolean | false;

    private totalCount: number | 0;

    private shopArea: string = "";

    private selectedMachine: string = "";

    componentDidLoad() {
        this.setUxTabElement();
        this.setScrollSensor();
        this.requestDataAndSetStatus(this.searchUrl, false, true);
    }

    private addSubmitEvents() {
        if (this.submitSelector === "") return;

        var selector = document.querySelectorAll(this.submitSelector);
        var selectorArr = Array.prototype.slice.call(selector);

        selectorArr.forEach((element) => {
            element.addEventListener("submit", (e) => {
                e.preventDefault();
                this.triggerRequestViaElement(e, element as HTMLElement);
            });
        });
    }

    private addClickEvents() {
        if (this.linkSelector === "") return;

        var selector = document.querySelectorAll(this.linkSelector);
        var selectorArr = Array.prototype.slice.call(selector);

        selectorArr.forEach((element) => {
            element.addEventListener("click", (e) => {
                e.preventDefault();
                this.triggerRequestViaElement(e, element as HTMLElement);
            });
        });
    }

    private addChangeEvents() {
        if (this.sortSelector === "") return;

        var selector = document.querySelectorAll(this.sortSelector);
        var selectorArr = Array.prototype.slice.call(selector);

        selectorArr.forEach((element) => {
            element.addEventListener("change", (e) => {
                this.triggerRequestViaElement(e, element as HTMLElement);
            });
        });
    }

    private setSearchResultContainer() {
        if (this.searchResultContainerSelector === "") return;
        this.searchResultContainer = document.querySelector(
            this.searchResultContainerSelector
        );
    }

    private setCategoryTreeContainer() {
        if (this.categoryTreeContainerSelector === "") return;
        this.categoryTreeContainer = document.querySelector(
            this.categoryTreeContainerSelector
        );
    }

    private setCategoryDescContainer() {
        if (this.categoryDescriptionSelector === "") return;
        this.categoryDescContainer = document.querySelector(
            this.categoryDescriptionSelector
        );
    }

    private setBreadCrumbContainer() {
        if (this.breadcrumbContainerSelector === "") return;
        this.breadCrumbContainer = document.querySelector(
            this.breadcrumbContainerSelector
        );
    }

    /** Connect total number */
    @Watch("uxTabSelector")
    setUxTabElement() {
        if (this.uxTabSelector === "") return;
        this.uxTabElement = document.querySelector(this.uxTabSelector);
    }

    /** connect scroll-sensor */
    @Watch("scrollSensorSelector")
    setScrollSensor() {
        if (this.scrollSensorSelector === "") return;
        this.scrollSensor = document.querySelector(
            this.scrollSensorSelector
        ) as HTMLUxInfiniteScrollSensorElement;
        if (this.scrollSensor !== null) {
            this.scrollSensor.addEventListener(
                "uxInfiniteScrollSensorScrolledIntoView",
                () => {
                    this.requestDataAndSetStatus(this.nextPage, true, false);
                }
            );
        }
    }

    private initializeCategoryTreeSelectors() {
        this.setBreadCrumbContainer();
        this.setCategoryDescContainer();
        this.setCategoryTreeContainer();
    }

    private updateTabNumber(total: string): void {
        if (this.uxTabElement == null) return;

        var totalNumberText = "(" + total.toString() + ")";

        if (this.totalNumberPrefix !== "") {
            totalNumberText =
                this.totalNumberPrefix + " (" + total.toString() + ")";
        }

        this.uxTabElement.name = totalNumberText;
    }

    private afterCompleteCall(url: string, isPaging: boolean): void {
        if (this.isCategoryTreeLoaded && this.isSearchResultPageLoaded) {
            if (!isPaging) {
                this.addSubmitEvents();
                this.addChangeEvents();
                this.addClickEvents();
            }

            this.afterDataSearchCall.emit({
                url: url,
                totalCount: this.totalCount,
                shopArea: this.shopArea,
                isPaging: isPaging,
                selectedMachine: this.selectedMachine,
            });
        }
    }

    private beforeDataCall(url: string, isPaging: boolean): void {
        this.beforeDataSearchCall.emit({
            url: url,
            isPaging: isPaging,
        });
    }

    private updateSearchResult(data: ProductSearchResponse): void {
        if (data == null || this.searchResultContainer == null) return;

        var divContainer = document.createElement("div");
        divContainer.innerHTML = data.searchResultHtml;

        if (data.currentPageNumber > 0) {
            const cssSearchResultSelector =
                ".category-or-search-result-container__result";
            var currentSearchResultContainer = document.querySelector(
                this.searchResultContainerSelector +
                    " " +
                    cssSearchResultSelector
            );

            if (currentSearchResultContainer == null) return;

            var updatedContent = divContainer.querySelector(
                cssSearchResultSelector
            );

            if (updatedContent) {
                currentSearchResultContainer.insertAdjacentHTML(
                    "beforeend",
                    updatedContent.innerHTML
                );
            }
        } else {
            this.updateAndReplaceElement(
                this.searchResultContainer,
                divContainer,
                this.searchResultContainerSelector
            );
        }
    }

    private updateContentFromCategoryTree(htmlResponse: any): void {
        if (htmlResponse == null) return;

        var divContainer = document.createElement("div");
        divContainer.innerHTML = htmlResponse;

        this.updateAndReplaceElement(
            this.categoryTreeContainer,
            divContainer,
            this.categoryTreeContainerSelector
        );
        this.updateAndReplaceElement(
            this.breadCrumbContainer,
            divContainer,
            this.breadcrumbContainerSelector
        );
        this.updateAndReplaceElement(
            this.categoryDescContainer,
            divContainer,
            this.categoryDescriptionSelector
        );
    }

    private updateAndReplaceElement(
        elementToUpdate: HTMLElement,
        updatedcontent: HTMLElement,
        elemetSelector: string
    ): void {
        if (elementToUpdate != null) {
            var updatedContentForElement = updatedcontent.querySelector(
                elemetSelector
            );

            if (updatedContentForElement) {
                elementToUpdate.replaceWith(updatedContentForElement);
            }
        }
    }

    private getSerializedFormData() {
        var serializedForm = [serializeForm(this.triggeredForm)]
            .filter((str) => str !== "")
            .join("&");

        return serializedForm;
    }

    private triggerRequestViaElement(event: Event, element: HTMLElement) {
        let targetURL: string;

        switch (event.type) {
            case "click":
                targetURL = element.getAttribute("href");
                this.requestDataAndSetStatus(targetURL, false, false);
                break;
            case "submit":
                this.triggeredForm = element as HTMLFormElement;
                targetURL = this.triggeredForm.getAttribute("action");
                this.requestDataAndSetStatus(targetURL, false, true);
                break;
            case "change":
                this.triggeredForm = element.closest("form") as HTMLFormElement;
                targetURL = this.triggeredForm.getAttribute("action");
                this.requestDataAndSetStatus(targetURL, false, true);
                break;
        }
    }

    private requestDataAndSetStatus(
        url: string,
        isPaging: boolean,
        isSerializationRequired: boolean
    ) {
        this.isCategoryTreeLoaded = isPaging ? true : false;
        this.isSearchResultPageLoaded = false;
        this.searchUrl = url;

        this.beforeDataCall(url, isPaging);
        this.requestProductData(url, isPaging, isSerializationRequired);

        if (!isPaging) {
            this.requestCategoryTree(url, isPaging, isSerializationRequired);
        }
    }

    private getOptionsForPostRequest(
        url: string,
        isCategoryTreeCall: boolean,
        isSerializationRequired: boolean
    ) {
        if (isMockUrl(url)) return {};

        var bodyValue = isSerializationRequired
            ? this.getSerializedFormData() +
              "&AjaxCall=true&PageType=" +
              this.pageType
            : "AjaxCall=true&PageType=" + this.pageType;

        if (isCategoryTreeCall) {
            bodyValue = bodyValue + "&CategoryTree=true";
        }

        return {
            method: "POST",
            body: bodyValue,
            headers: {
                "Content-Type":
                    "application/x-www-form-urlencoded;charset=UTF-8",
            },
        };
    }

    /** Send the real request and handle response */
    private requestProductData(
        url: string,
        isPaging: boolean,
        isSerializationRequired: boolean
    ) {
        if (url === "") return;

        api<ProductSearchResponse>(
            url,
            this.getOptionsForPostRequest(url, false, isSerializationRequired)
        ).then((data) => {
            this.nextPage = data.nextPage || "";
            this.setSearchResultContainer();
            this.updateSearchResult(data);
            this.updateTabNumber(data.totalCount.toString());

            this.isSearchResultPageLoaded = true;
            this.shopArea = data.shopArea;
            this.totalCount = data.totalCount;
            this.selectedMachine = data.selectedMachine;

            this.afterCompleteCall(url, isPaging);

            if (this.nextPage !== "" && this.scrollSensor !== null) {
                this.scrollSensor.hasFired = false;
            }
        });
    }

    private requestCategoryTree(
        url: string,
        isPaging: boolean,
        isSerializationRequired: boolean
    ) {
        if (url === "") return;

        if (isMockUrl(url)) {
            url = url.replace("page.json", "category-tree.html");
        }

        api<any>(
            url,
            this.getOptionsForPostRequest(url, true, isSerializationRequired)
        ).then((data) => {
            this.initializeCategoryTreeSelectors();
            this.updateContentFromCategoryTree(data);
            this.isCategoryTreeLoaded = true;
            this.afterCompleteCall(url, isPaging);
        });
    }

    render() {
        return null;
    }
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}
